import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFire } from '@fortawesome/free-solid-svg-icons';
import api from '../config/api';

interface HeatmapData {
  topic: string;
  totalQuestions: number;
  correctAnswers: number;
  accuracy: string;
  performanceScore: number;
}

interface HeatmapProps {
  examId: number;
}

const Heatmap: React.FC<HeatmapProps> = ({ examId }) => {
  const [heatmapData, setHeatmapData] = useState<HeatmapData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHeatmap();
  }, [examId]);

  const fetchHeatmap = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/stats/heatmap/${examId}`);
      setHeatmapData(response.data.heatmap);
    } catch (error) {
      console.error('Failed to fetch heatmap:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTileStyle = (score: number): React.CSSProperties => {
    if (score >= 80) {
      return {
        background: 'var(--gp-success-soft)',
        borderColor: 'var(--gp-success)',
        color: 'var(--gp-success)'
      };
    }
    if (score >= 60) {
      return {
        background: 'var(--gp-accent-soft)',
        borderColor: 'var(--gp-accent)',
        color: 'var(--gp-accent)'
      };
    }
    if (score >= 40) {
      return {
        background: '#fff7ed',
        borderColor: '#ea580c',
        color: '#c2410c'
      };
    }
    return {
      background: '#fef2f2',
      borderColor: '#ef4444',
      color: '#b91c1c'
    };
  };

  if (loading) {
    return (
      <div className="gp-card">
        <div className="flex items-center justify-center py-8">
          <div className="gp-spinner" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (heatmapData.length === 0) {
    return (
      <div className="gp-card">
        <h3 className="text-base font-bold mb-3 flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
          <FontAwesomeIcon icon={faFire} style={{ color: 'var(--gp-accent)' }} aria-hidden="true" />
          Performance Heatmap
        </h3>
        <p className="text-sm gp-text-muted text-center py-4">
          Complete some practice tests to see your performance breakdown by topic.
        </p>
      </div>
    );
  }

  return (
    <div className="gp-card">
      <h3 className="text-base font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
        <FontAwesomeIcon icon={faFire} style={{ color: 'var(--gp-accent)' }} aria-hidden="true" />
        Performance Heatmap by Topic
      </h3>
      
      <div
        className="flex flex-wrap gap-3 mb-4 pb-4"
        style={{ borderBottom: '1px solid var(--gp-border)' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded"
            style={{ background: 'var(--gp-success)', border: '1px solid var(--gp-success)' }}
          />
          <span className="text-xs gp-text-muted">Excellent (80%+)</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded"
            style={{ background: 'var(--gp-accent)', border: '1px solid var(--gp-accent)' }}
          />
          <span className="text-xs gp-text-muted">Good (60-79%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded"
            style={{ background: '#ea580c', border: '1px solid #c2410c' }}
          />
          <span className="text-xs gp-text-muted">Average (40-59%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded"
            style={{ background: '#ef4444', border: '1px solid #b91c1c' }}
          />
          <span className="text-xs gp-text-muted">Needs Improvement (&lt;40%)</span>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {heatmapData.map((item) => (
          <div
            key={item.topic}
            className="p-3 rounded-md border-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
            style={getTileStyle(item.performanceScore)}
            title={`${item.topic}: ${item.accuracy}% accuracy (${item.correctAnswers}/${item.totalQuestions})`}
          >
            <div className="text-center">
              <div className="font-semibold text-xs mb-1 truncate">{item.topic}</div>
              <div className="text-xl font-bold mb-0.5">{item.accuracy}%</div>
              <div className="text-[11px] opacity-80">
                {item.correctAnswers}/{item.totalQuestions}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Heatmap;
